import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@components/button';
import { createForm } from '@components/form';
import { IconAddPlus } from '@components/icons/add-plus';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';
import { disabledSectionStyle } from '@styles/theme';

import { ModalProps } from 'features/modals';
import { useStore } from 'models/Provider';

import {
  Cosigner,
  CosignerError,
  EditCosigner,
  taktikalAddCosigner,
  taktikalEditCosigner,
} from './api';

const Form = createForm<EditCosigner>();

export const TaktikalCosignerModal = (props: ModalProps<{ cosigner?: Cosigner }>) => {
  const { t } = useStore();
  const { showError, showSuccess } = useToaster();
  const { closeModal, cosigner } = props;

  const client = useQueryClient();

  const isEdit = cosigner !== undefined;

  const submit = useMutation(
    (data: EditCosigner) =>
      isEdit ? taktikalEditCosigner(cosigner.id.toString(), data) : taktikalAddCosigner(data),
    {
      onSuccess: () => {
        const message = isEdit
          ? t('integrations.taktikal.settings.cosigners.edit.success')
          : t('integrations.taktikal.settings.cosigners.add.success');
        showSuccess(message);
        client.resetQueries(['taktikal-cosigners']);
        closeModal();
      },
      onError: (data: CosignerError) => {
        if (data && data.response && data.response.data && data.response.data.nonFieldErrors) {
          showError(data.response.data.nonFieldErrors.join(' '));
        } else {
          showError(t('integrations.taktikal.settings.cosigner.modal.error'));
        }
      },
    }
  );

  return (
    <Stack space={6} style={submit.isLoading ? disabledSectionStyle : undefined}>
      <Text
        font="heading2"
        text={
          isEdit
            ? t('integrations.taktikal.settings.cosigners.modal.edit.header')
            : t('integrations.taktikal.settings.cosigners.modal.header')
        }
      />
      <Form onSubmit={(values) => submit.mutateAsync(values)}>
        <Form.TextField
          label={t('integrations.taktikal.settings.cosigners.modal.name.label')}
          id="name"
          validators={['required']}
          defaultValue={cosigner ? cosigner.name : ''}
        />
        <Form.TextField
          label={t('integrations.taktikal.settings.cosigners.modal.email.label')}
          id="email"
          validators={['required', 'email']}
          defaultValue={cosigner ? cosigner.email : ''}
        />
        <Form.TextField
          label={t('integrations.taktikal.settings.cosigners.modal.ssn.label')}
          id="ssn"
          validators={['required', 'ssn']}
          defaultValue={cosigner ? cosigner.ssn : ''}
        />

        <Form.TextField
          label={t('integrations.taktikal.settings.cosigners.modal.phone.label')}
          id="phone"
          validators={['required', 'phone']}
          defaultValue={cosigner ? cosigner.phone : '+354'}
        />
        <Form.Actions>
          <Button
            variant="secondary"
            text={t('integrations.taktikal.settings.cosigners.modal.cancel')}
            onClick={closeModal}
          />
          <Form.Submit
            text={
              isEdit
                ? t('integrations.taktikal.settings.cosigners.modal.save')
                : t('integrations.taktikal.settings.cosigners.modal.submit')
            }
            iconLeft={isEdit ? undefined : <IconAddPlus />}
          />
        </Form.Actions>
      </Form>
    </Stack>
  );
};
